import { useMemo } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { DDELicenseResponse } from '@img-arena/consoles-api-dde'
import { DDELicenseForm } from '@/components/forms/LicenseForm/LicenseForm.dde'
import {
  ActionFormItem,
  ActionMenuExportToCSVItem,
  ActionsMenu,
  ActionToggleOperatorLicenseSuspensionItem
} from '@/components/table/actions'
import { FilterableTable } from '@/components/table/base'
import { STORE_STATUS } from '@/constants/store'
import { ddeApi } from '@/service/ddeApi'
import { useOperatorLicensesStoreDDE } from '@/stores/operator/licenses.dde.store'

import {
  CSV_HEADERS,
  DDE_TABLE_COLUMNS,
  formatLicenseCsvRow,
  getDDETableFilters,
  getLicenseTableRows,
  LICENSE_STATUSES,
  LicenseRow
} from './licenses.dde.table'

export const DDE_OPERATOR_LICENSES_TABLE_ID = 'dde-operator-licenses-table'

export const LicensesViewDDE = () => {
  const { operatorId } = useParams()
  const numericOperatorId = operatorId ? Number(operatorId) : undefined

  const { data, isFetching, isLoading, fetchData } = useOperatorLicensesStoreDDE(
    useShallow((state) => ({
      data: state.data,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching,
      fetchData: state.fetchData
    }))
  )

  const tableRows = useMemo(() => getLicenseTableRows(data), [data])

  const tableFilters = useMemo(() => getDDETableFilters(tableRows), [tableRows])

  const handleOperatorLicenseSuspensionToggle = async (row: LicenseRow) => {
    if (!numericOperatorId) return
    if (row.status.type === LICENSE_STATUSES.SUSPENDED) {
      await ddeApi.endpoints.operators.unsuspendOperatorLicense(numericOperatorId, row.property)
    } else {
      await ddeApi.endpoints.operators.suspendOperatorLicense(numericOperatorId, row.property)
    }
    await fetchData({ operatorId: numericOperatorId })
  }

  if (!operatorId) return null

  return (
    <FilterableTable
      tableId={DDE_OPERATOR_LICENSES_TABLE_ID}
      columns={DDE_TABLE_COLUMNS}
      filters={tableFilters}
      data={tableRows}
      loading={isLoading}
      disableColumnVisibility
      withCheckboxes
      fetching={isFetching}
      clientSideFilters
      defaultSort={{
        attribute: 'year',
        order: 'desc'
      }}
      clientSideSortAndPagination
      rowActions={
        <ActionsMenu>
          <ActionFormItem<LicenseRow>
            getForm={(row) => (
              <DDELicenseForm
                defaultValues={row}
                onSubmit={() => fetchData({ operatorId: Number(operatorId) })}
                data={{
                  operatorId: Number(operatorId),
                  licenseProperty: row.property,
                  operatorLicenses: data as DDELicenseResponse[]
                }}
              />
            )}
          />

          <ActionToggleOperatorLicenseSuspensionItem
            onClick={handleOperatorLicenseSuspensionToggle}
          />
          <ActionMenuExportToCSVItem
            csvHeaders={CSV_HEADERS}
            formatCsvRow={formatLicenseCsvRow}
            csvFileName={(row: LicenseRow) => `operator_${operatorId}_${row.property}`}
          />
        </ActionsMenu>
      }
    />
  )
}
